/**
 * Tareas en segundo plano (no bloqueantes) — contexto global.
 *
 * Permite lanzar trabajo asíncrono (p.ej. ejecutar un diagnóstico o certificar
 * una rama) SIN bloquear la UI: la promesa corre, el usuario puede navegar, y
 * un indicador en el topbar gira mientras haya tareas activas. Al terminar,
 * se lanza un toast (éxito/error) con el resultado.
 *
 * Uso:
 *   const { run } = useBackgroundTasks();
 *   const result = await run({ label: "Diagnóstico HT_CON_012", fn: () => runByRef(...) });
 *
 * `run` devuelve la misma promesa de `fn` (re-lanza el error) para que el caller
 * pueda seguir manejando el resultado localmente si lo necesita; el toast y el
 * indicador global son un efecto colateral.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useToast } from "./Toast";

export type TaskStatus = "running" | "done" | "error";

export interface BackgroundTask {
  id: number;
  label: string;
  status: TaskStatus;
  startedAt: number;
  finishedAt?: number;
  detail?: string;
}

interface RunOptions<T> {
  /** Etiqueta visible en el indicador y el toast (p.ej. "Diagnóstico HT_CON_012"). */
  label: string;
  /** Trabajo asíncrono a ejecutar. */
  fn: () => Promise<T>;
  /** Mensaje de éxito del toast. Si recibe el resultado, puede derivarlo. */
  successMessage?: (result: T) => string;
  /** Mensaje de error del toast. Por defecto usa el detalle del error. */
  errorMessage?: (err: unknown) => string;
  /** Si false, no muestra toast al terminar (default true). */
  toast?: boolean;
}

interface BackgroundTasksValue {
  tasks: BackgroundTask[];
  activeCount: number;
  run: <T>(opts: RunOptions<T>) => Promise<T>;
  clearFinished: () => void;
}

const Ctx = createContext<BackgroundTasksValue | null>(null);

let nextTaskId = 1;
// Cuántas tareas terminadas conservar en el historial del popover.
const MAX_HISTORY = 8;

function errDetail(err: unknown): string {
  const anyErr = err as { response?: { data?: { detail?: string } }; message?: string };
  return anyErr?.response?.data?.detail ?? anyErr?.message ?? "Error desconocido";
}

export function BackgroundTasksProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  // now() sin Date para evitar rarezas; usamos performance.now redondeado.
  const seq = useRef(0);
  const stamp = () => (seq.current += 1);

  const run = useCallback(
    async <T,>(opts: RunOptions<T>): Promise<T> => {
      const id = nextTaskId++;
      const startedAt = stamp();
      setTasks((prev) => [
        { id, label: opts.label, status: "running" as TaskStatus, startedAt },
        ...prev,
      ]);
      const settle = (status: TaskStatus, detail?: string) =>
        setTasks((prev) => {
          const updated = prev.map((t) =>
            t.id === id ? { ...t, status, finishedAt: stamp(), detail } : t,
          );
          // Recorta el historial de terminadas, conserva las activas.
          const running = updated.filter((t) => t.status === "running");
          const finished = updated.filter((t) => t.status !== "running").slice(0, MAX_HISTORY);
          return [...running, ...finished];
        });

      try {
        const result = await opts.fn();
        const msg = opts.successMessage?.(result) ?? "Proceso finalizado";
        settle("done", msg);
        if (opts.toast !== false) {
          showToast({ kind: "success", title: opts.label, message: msg });
        }
        return result;
      } catch (err) {
        const msg = opts.errorMessage?.(err) ?? errDetail(err);
        settle("error", msg);
        if (opts.toast !== false) {
          showToast({ kind: "error", title: opts.label, message: msg, durationMs: 8000 });
        }
        throw err;
      }
    },
    [showToast],
  );

  const clearFinished = useCallback(
    () => setTasks((prev) => prev.filter((t) => t.status === "running")),
    [],
  );

  const activeCount = tasks.filter((t) => t.status === "running").length;

  return (
    <Ctx.Provider value={{ tasks, activeCount, run, clearFinished }}>
      {children}
    </Ctx.Provider>
  );
}

export function useBackgroundTasks(): BackgroundTasksValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useBackgroundTasks debe usarse dentro de <BackgroundTasksProvider>");
  return ctx;
}
